import { Currency, defaultCostCategories, DistanceUnit, FileData, getCurrencies, IOSMResponse } from "@eventr/common";
import i18n from "i18n/i18next";
import { bs } from "state/bound.store";
import { z } from "zod";
import type { RoutedDistance } from "./distance/route-distance";

export interface StandardExpenseFormValues {
  fileData?: FileData | null;
  amount: string;
  currency: string;
  description?: string;
  merchant?: string;
  invoiceDate: string;
  category: string;
  section: number;
  costCenter?: string;
}

export interface DistanceExpenseFormValues {
  distance: string;
  rate: string;
  distanceUnit: DistanceUnit;
  currency: string;
  numberOfPassengers?: string;
  description?: string;
  invoiceDate: string;
  category: string;
  section: number;
  costCenter?: string;
  startLocation?: IOSMResponse;
  endLocation?: IOSMResponse;
  /** Form-only: what the distance was last routed from. Not sent with the expense. */
  routedDistance?: RoutedDistance;
}

const emptyToUndefined = (value: string | undefined) =>
  typeof value === "string" && value.trim() === "" ? undefined : value;

export const getStandardExpenseFormSchema = () =>
  z.object({
    fileData: z
      .object({
        base64: z.string(),
        mimeType: z.string(),
        filename: z.string(),
      })
      .nullable()
      .optional(),
    amount: z.string().min(1, {
      message: i18n.t("edit-itinerary.errorMessage.input-is-required"),
    }),
    currency: z.string(),
    description: z.string().optional().transform(emptyToUndefined),
    merchant: z.string().optional().transform(emptyToUndefined),
    invoiceDate: z.string(),
    category: z.string(),
    section: z.number(),
    costCenter: z.string().optional().transform(emptyToUndefined),
  });

export const getDistanceExpenseFormSchema = () =>
  z.object({
    distance: z.string().min(1, {
      message: i18n.t("edit-itinerary.errorMessage.input-is-required"),
    }),
    rate: z.string().min(1, {
      message: i18n.t("edit-itinerary.errorMessage.input-is-required"),
    }),
    distanceUnit: z.enum(["mi", "km"]),
    currency: z.string(),
    numberOfPassengers: z.string().optional().transform(emptyToUndefined),
    description: z.string().optional().transform(emptyToUndefined),
    invoiceDate: z.string(),
    category: z.string(),
    section: z.number(),
    costCenter: z.string().optional().transform(emptyToUndefined),
    startLocation: z.any().optional(),
    endLocation: z.any().optional(),
    routedDistance: z.any().optional(),
  });

export function isDistanceExpense(
  formValues: StandardExpenseFormValues | DistanceExpenseFormValues,
): formValues is DistanceExpenseFormValues {
  return "distance" in formValues;
}

/** Currencies the guest expense picker offers: the `coordinator` area, subject to the org's override list. */
export function getCurrencyList(): Currency[] {
  return getCurrencies({
    area: "coordinator",
    allowed: bs().orgConfig.currencyAreas?.coordinator,
  });
}

export function getCategoryList() {
  const categoryList: {
    id: string;
    label: string;
  }[] = Object.entries(defaultCostCategories)
    .filter(([key]) => !isNaN(Number(key)))
    .map(([key, value]) => ({ id: key, label: i18n.t(`cost.categories.${String(value)}`) }));

  return categoryList;
}

export function getSectionList() {
  const sectionList = bs().sections.map((section) => ({
    id: section.id,
    displayName: i18n.t(`cost.sections.${section.id}.name`),
  }));

  return sectionList;
}
